import React, { useEffect, useRef, useState } from "react";
import { useDispatch, batch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
	ApiService,
	CustomizableProjectPagesService,
	LicenceManagementService,
	MyService,
	UserService,
} from "../../api";
import {
	getFlags,
	getProject,
	getSegFlags,
	getUser,
	getPreferedLanguage,
	resetProject,
	getAllFavoriteCategories,
	getTeamMembers,
	setCustomFormProjectDetails,
	setloggedPhysicalComp,
	getCurrentUserProfilePicture,
	getHasAvailableAIQuota,
} from "../../common/slice";
import Navbar from "./navbar/Navbar";
import KeycloakService from "../../services/KeycloakService";
import { commonMenu, homeMenu, mentionsMenu, projectManagementMenu, projectMenu } from "../menus";
import {
	environmentAllowed,
	environmentSwitch,
	isFeatureEnabled,
	isSegFeatureEnabled,
	loadNavTranslation,
	Flags,
	SegFlags,
	setHasCommitmentAccess,
	hasPermission,
	Permissions,
} from "../../common/providers";
import { getProjectIdFromURL, NavUtils } from "..";
import superstore from "../../store";
import { useApi } from "../../common/hooks";

const NavBarContainer = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { value } = useSelector(({ context }) => context.language);
	const portfolioName = useSelector(({ context }) => context?.portfolioInfo?.name);
	const companyName = useSelector(({ context }) => context.company?.name);
	const { project, company, segFlags, companiesToProject, loadingFlags } = useSelector(
		({ context }) => ({
			project: context.project,
			company: context.company,
			segFlags: context.segFlags,
			companiesToProject: context.companiesToProject,
			loadingFlags: context.loadingFlags,
		}),
		shallowEqual
	);
	const isLogged = KeycloakService.isLoggedIn();
	const preventNavigation = useSelector(({ context }) => context.preventNavigation);
	const language = useSelector(({ context }) => context.language?.value);
	const companies = useSelector(({ context }) => context.companies);
	const loggedPhysicalCompId = useSelector(({ context }) => context.loggedPhysicalComp);
	const reduxProjectId = useSelector(({ context }) => context?.project?.id);
	const flags = useSelector(({ context }) => context.flags);
	const user = useSelector(({ context }) => context.user);
	const [commitmentAccessLoaded, setCommitmentAccessLoaded] = useState(false);
	const [currentOpenedMenu, setCurrentOpenedMenu] = useState("");
	const [mentionsMenus, setMentionsMenus] = useState(null);
	const [projectMenus, setProjectMenus] = useState(null);
	const [projectManagementMenus, setProjectManagementMenus] = useState(null);
	const [render, setRender] = useState(false);
	const [url, setUrl] = useState("/");
	const [projectId, setProjectId] = useState(null);
	const [openAiConsumption, setOpenAiConsumption] = useState(false);
	const cancelTokenSourceRef = useRef(null);
	const { call: getCustomPageProjectDetails } = useApi(CustomizableProjectPagesService.getCustomPageProjectDetails);
	const { call: getUserCommitmentAccess } = useApi(LicenceManagementService.getUserCommitmentAccess);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		const currentUrl = location.pathname;
		setUrl(currentUrl);
		const matchedID = getProjectIdFromURL(currentUrl);
		if (matchedID && matchedID !== "join") {
			setProjectId(matchedID);
		} else {
			setProjectId(null);
		}
	}, [location.pathname]);
	useEffect(() => {
		const cancelToken = ApiService.getCancelTokenSource();
		if (isLogged) {
			setTimeout(() => {
				batch(() => {
					dispatch(getFlags(cancelToken.token));
					dispatch(getUser(cancelToken.token));
					dispatch(getPreferedLanguage(cancelToken.token));
				});
			}, 100);
		}
		return () => {
			ApiService.cancelTokens(cancelToken);
		};
	}, [isLogged, language, projectId, dispatch]);
	useEffect(() => {
		if (!isLogged || !flags || !isFeatureEnabled(Flags.PROFILE_PICTURE)) {
			return undefined;
		}
		const cancelToken = ApiService.getCancelTokenSource();
		dispatch(getCurrentUserProfilePicture(cancelToken.token));
		return () => {
			ApiService.cancelTokens(cancelToken);
		};
	}, [isLogged, flags, dispatch]);
	useEffect(() => {
		const cancelToken = ApiService.getCancelTokenSource();
		if (projectId && !loadingFlags && isFeatureEnabled(Flags.MENTIONS)) {
			dispatch(getTeamMembers({ projectId, token: cancelToken.token }));
		}
		return () => {
			ApiService.cancelTokens(cancelToken);
		};
	}, [projectId, loadingFlags, dispatch]);
	useEffect(() => {
		const cancelToken = ApiService.getCancelTokenSource();
		if (
			companyName &&
			!loadingFlags &&
			!hasPermission(Permissions.PLATFORM_ADMIN) &&
			isFeatureEnabled(Flags.AI_QUOTA)
		) {
			dispatch(getHasAvailableAIQuota({ token: cancelToken.token }));
		}
		return () => {
			ApiService.cancelTokens(cancelToken);
		};
	}, [loadingFlags, dispatch, companyName]);
	// main project reset
	useEffect(() => {
		if (environmentAllowed(["dev", "qa", "prod"])) {
			const cancelToken = ApiService.getCancelTokenSource();
			if (isLogged && projectId && projectId !== "undefined") {
				batch(() => {
					dispatch(getProject({ projectId, token: cancelToken.token }));
					dispatch(getSegFlags({ projectId, token: cancelToken.token }));
				});
			} else if (projectId !== "undefined") {
				dispatch(resetProject(null));
			}
			return () => {
				ApiService.cancelTokens(cancelToken);
			};
		}
		return undefined;
	}, [isLogged, projectId, dispatch]);
	// Veolia project reset
	useEffect(
		() => {
			if (environmentAllowed(["veolia"])) {
				const cancelToken = ApiService.getCancelTokenSource();
				const isInProject = location.pathname.includes("/projects");
				const pid = Number.parseInt(projectId, 10);
				MyService.getDetails(cancelToken.token)
					.then((userDetailsData) => {
						if (isInProject) {
							if (
								isLogged &&
								projectId &&
								companiesToProject?.projectId === pid &&
								(companiesToProject?.companies || []).includes(userDetailsData.companyId)
							) {
								batch(() => {
									dispatch(getProject({ projectId: pid, token: cancelToken.token }));
									getCustomPageProjectDetails({ projectId })
										.then((data) => {
											dispatch(setCustomFormProjectDetails(data));
										})
										.catch(console.error);
									dispatch(getSegFlags({ projectId: pid, token: cancelToken.token }));
								});
							} else {
								dispatch(resetProject(null));
							}
						}
					})
					.catch(console.error);
				if (!isInProject && !pid && reduxProjectId) {
					dispatch(resetProject(null));
				}
				return () => {
					ApiService.cancelTokens(cancelToken);
				};
			}
			return undefined;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isLogged, projectId, companiesToProject, company, dispatch]
	);
	useEffect(() => {
		if (projectId && SegFlags && isSegFeatureEnabled(SegFlags.FAVORITE_CATEGORIES)) {
			dispatch(getAllFavoriteCategories({ projectId, token: cancelTokenSourceRef.current.token }));
		}
	}, [segFlags, dispatch, projectId]);
	// Fetch commitment access for current user (Veolia only)
	useEffect(() => {
		const isInLicenceManagement = location.pathname.startsWith("/licence-management");
		const isInProject = location.pathname.startsWith("/projects");
		if (
			isLogged &&
			user?.id &&
			environmentAllowed(["veolia"]) &&
			(isInLicenceManagement || (isInProject && project?.id))
		) {
			getUserCommitmentAccess({ userId: user.id })
				.then((data) => {
					setHasCommitmentAccess(data?.value || false);
					setCommitmentAccessLoaded(true);
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLogged, user?.id, project]);
	useEffect(() => {
		if (project) {
			environmentSwitch({
				main: setProjectMenus(projectMenu({ projectId: project.id })),
				veolia: setProjectMenus(
					projectMenu({
						projectId: project.id,
						portfolioName: (portfolioName || "")?.slice(0, -1),
					})
				),
			});
			setProjectManagementMenus(projectManagementMenu({ projectId: project.id }));
			setMentionsMenus(mentionsMenu({ projectId: project.id }));
		}
	}, [project, portfolioName, companyName, commitmentAccessLoaded]);
	useEffect(() => {
		loadNavTranslation(value)
			.then(() => setRender(true))
			.catch((err) => console.error(err));
		return () => setRender(false);
	}, [value]);
	const handleOpen = (menuValue) => {
		if (currentOpenedMenu !== menuValue) {
			setCurrentOpenedMenu(menuValue);
		} else {
			setCurrentOpenedMenu("");
		}
	};
	const handleOpenHover = (menuValue) => {
		if (currentOpenedMenu) {
			setCurrentOpenedMenu(menuValue);
		}
	};
	const handleClose = () => {
		setCurrentOpenedMenu("");
	};
	const handleSelect = (menu) => {
		navigate(menu.to, { state: { previous: location.pathname } });
		handleClose();
	};
	const handleHomeSelect = () => {
		window.history.pushState({}, "", "/");
	};
	const handleClickLogout = () => {
		if (!preventNavigation.active) {
			const cancelToken = ApiService.getCancelTokenSource().token;
			UserService.logout(cancelToken).finally(() => {
				superstore.persistor.purge();
				handleHomeSelect();
				KeycloakService.doLogout().then(() => {
					KeycloakService.clearToken();
				});
			});
		}
	};
	const handleIsSelected = (menuUrl) => {
		if (menuUrl === "/" && url !== "/") {
			return false;
		}
		if (url === menuUrl || url?.startsWith(`${menuUrl}/`)) {
			return true;
		}
		return false;
	};
	const handleIncludes = (array, i = 0) => {
		let count = i;
		if (Array.isArray(array)) {
			array.forEach((menu) => {
				if (url?.startsWith(menu.to)) {
					count++;
				}
				if (menu.subItems) {
					count = handleIncludes(menu.subItems, count);
				}
			});
		}
		return count;
	};
	const handleOpenAiConsumption = () => {
		setOpenAiConsumption(true);
		handleClose();
	};
	const handleCloseAiConsumption = () => {
		setOpenAiConsumption(false);
	};
	const handleHotRelog = (companyToRelog) => {
		handleClose();
		if (!companyToRelog) {
			return;
		}
		if (companyToRelog.companyName === "VEOLIA EAU" && environmentAllowed(["veolia"])) {
			if (window.location.pathname === "/licence-management") {
				window.history.pushState({}, "", NavUtils.goToHome());
			} else {
				window.history.pushState({}, "", NavUtils.goToLicenceManagement());
			}
		}
		UserService.loginWithCompany({ companyId: companyToRelog.id }, cancelTokenSourceRef.current.token)
			.then(() => {
				dispatch(
					setloggedPhysicalComp(
						companies.find((c) => c.id === companyToRelog?.id)?.id || loggedPhysicalCompId
					)
				);
				window.location.reload();
			})
			.catch((err) => console.error(err));
	};
	return render ? (
		<Navbar
			commonMenus={commonMenu}
			company={company}
			currentOpenedMenu={currentOpenedMenu}
			homeMenus={homeMenu}
			isLogged={isLogged}
			mentionsMenus={mentionsMenus}
			openAiConsumption={openAiConsumption}
			project={project}
			projectManagementMenus={projectManagementMenus}
			projectMenus={projectMenus}
			onClickLogout={handleClickLogout}
			onClose={handleClose}
			onCloseAiConsumption={handleCloseAiConsumption}
			onHotRelog={handleHotRelog}
			onIncludes={handleIncludes}
			onIsSelected={handleIsSelected}
			onOpen={handleOpen}
			onOpenAiConsumption={handleOpenAiConsumption}
			onOpenHover={handleOpenHover}
			onSelect={handleSelect}
		/>
	) : null;
};
export default NavBarContainer;
